import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import LoadingSpinner from "../../components/LoadingSpinner";
import { formatSwedishTime } from "../../helpers/DateTime";
import toast from "react-hot-toast";
import EntityMultiSelectForm from "../../components/EntityMultiSelectForm";
import ExternalUserForm from "../../components/externalUser/ExternalUserForm";
import {
    mapOrganizationsToOptions,
    shouldExternalUserBelongToOrganization,
} from "../../components/externalUser/externalUserUtils";
import { getAllCustomers } from "../../api/Customers";
import {
    getCustomerDisplayName,
    shouldShowOrganizationName,
} from "../../helpers/CustomerDisplay";
import { getAllOrganizations } from "../../api/Resources";
import {
    getExternalUserByPublicId,
    updateExternalUser,
} from "../../api/ExternalUsers";

const EditUserPage = () => {
    const { t } = useTranslation();
    const { getAccessToken, hasRole } = useAuth();
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();

    const query = new URLSearchParams(location.search);
    const [activeTab, setActiveTab] = useState(query.get("tab") || "details");

    const [loading, setLoading] = useState(true);
    const [hasChanges, setHasChanges] = useState(false);

    const [user, setUser] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phonePrefix: "",
        phone: "",
        externalUserRole: "",
        organizationPublicId: "",
        createdDateTimeUtc: "",
    });
    const [originalUser, setOriginalUser] = useState(null);

    const [organizationOptions, setOrganizationOptions] = useState([]);
    const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);
    const [originalCustomerIds, setOriginalCustomerIds] = useState([]);

    useEffect(() => {
        const nextQuery = new URLSearchParams(location.search);
        nextQuery.set("tab", activeTab);
        navigate(`${location.pathname}?${nextQuery.toString()}`, { replace: true });
    }, [activeTab, location.pathname, location.search, navigate]);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const token = await getAccessToken();

                const [externalUser, organizations] = await Promise.all([
                    getExternalUserByPublicId(id, token),
                    getAllOrganizations(token),
                ]);

                const normalizedUser = {
                    firstName: externalUser?.firstName ?? "",
                    lastName: externalUser?.lastName ?? "",
                    email: externalUser?.email ?? "",
                    phonePrefix: externalUser?.phonePrefix ?? "",
                    phone: externalUser?.phone ?? "",
                    createdDateTimeUtc: formatSwedishTime(
                        externalUser?.createdDateTimeUtc
                    ) ?? "",
                    externalUserRole: externalUser?.externalUserRole ?? "",
                    organizationPublicId:
                        externalUser?.organization?.organizationPublicId ?? "",
                    termsAccepted: externalUser?.termsAccepted === true,
                };

                const customerIds =
                    (externalUser?.customers || [])
                        .filter((customer) => !customer?.isDeleted)
                        .map((customer) => customer?.customerPublicId)
                        .filter(Boolean) || [];

                setUser(normalizedUser);
                setOriginalUser(normalizedUser);
                setSelectedCustomerIds(customerIds);
                setOriginalCustomerIds(customerIds);
                setOrganizationOptions(mapOrganizationsToOptions(organizations));
            } catch (err) {
                console.error(err);
                toast.error(t("toast.failedLoad", { entity: t("entity.user") }));
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [getAccessToken, id]);

    useEffect(() => {
        if (!originalUser) {
            return;
        }

        const userChanged =
            user.organizationPublicId !== originalUser.organizationPublicId;

        const normalize = (values) => [...(values || [])].sort();
        const customersChanged =
            JSON.stringify(normalize(selectedCustomerIds)) !==
            JSON.stringify(normalize(originalCustomerIds));

        setHasChanges(userChanged || customersChanged);
    }, [user, originalUser, selectedCustomerIds, originalCustomerIds]);

    const navigateBack = () => {
        navigate("/externalUsers");
    };

    const handleInputChange = (field, value) => {
        setUser((prev) => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        try {
            const token = await getAccessToken();

            await updateExternalUser(
                id,
                {
                    externalUserPublicId: id,
                    customerPublicIds: (selectedCustomerIds || []).filter(Boolean),
                    organizationPublicId: shouldExternalUserBelongToOrganization(
                        user.externalUserRole
                    )
                        ? user.organizationPublicId || null
                        : null,
                },
                token
            );

            setOriginalUser(user);
            setOriginalCustomerIds([...(selectedCustomerIds || [])]);
            toast.success(t("toast.updated", { entity: t("entity.user") }));
        } catch (err) {
            console.error(err);
            toast.error(err?.message || t("toast.failedUpdate", { entity: t("entity.user") }));
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen w-full flex items-center justify-center">
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <>
            <HeaderBar />
            <div className="flex flex-row w-full">
                <Sidebar />
                <main className="page">
                    <div className={`${loading ? "loading" : ""} page-content`}>
                        <div className="flex flex-row items-center pb-4">
                            <button onClick={navigateBack} className="text-black flex">
                                <FontAwesomeIcon icon={faArrowLeft} className="w-4 pr-4" />
                            </button>

                            <h2 className="border-l border-mute-200 pl-4">
                                {user.firstName} {user.lastName}
                            </h2>

                            <div className="flex gap-4 ml-auto">
                                <button
                                    className="btn btn-outline-primary"
                                    onClick={navigateBack}
                                >
                                    {t("common.cancel")}
                                </button>
                                <button
                                    className="btn btn-primary"
                                    disabled={!hasChanges || loading}
                                    onClick={handleSave}
                                >
                                    {t("common.save")}
                                </button>
                            </div>
                        </div>

                        <div className="px-4 py-8 shadow-lg rounded-md w-full">
                            <div className="border-b border-mute-200 mb-4 gap-4 flex">
                                <button
                                    className={`px-4 pb-2 ${
                                        activeTab === "details"
                                            ? "border-b-2 border-black font-bold text-sm text-black"
                                            : "text-mute-400 text-sm font-bold"
                                    }`}
                                    onClick={() => setActiveTab("details")}
                                >
                                    {t("tabs.details")}
                                </button>
                            </div>

                            {activeTab === "details" && (
                                <div className="flex flex-col gap-8">
                                    <ExternalUserForm
                                        user={user}
                                        organizationOptions={organizationOptions}
                                        onChange={handleInputChange}
                                        editableFields={{
                                            organizationPublicId: true,
                                        }}
                                        showCreated
                                    />

                                    <EntityMultiSelectForm
                                        title={t("entity.customers")}
                                        fetchItems={async (token) => {
                                            const list = await getAllCustomers(token);
                                            return (list || []).map((customer) => ({
                                                ...customer,
                                                customerPublicId: customer.customerPublicId,
                                                customerName: getCustomerDisplayName(
                                                    customer,
                                                    shouldShowOrganizationName(hasRole)
                                                ),
                                            }));
                                        }}
                                        labelKey="customerName"
                                        valueKey="customerPublicId"
                                        selectedIds={selectedCustomerIds}
                                        onChange={(ids) =>
                                            setSelectedCustomerIds((ids || []).filter(Boolean))
                                        }
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </main>
            </div>
        </>
    );
};

export default EditUserPage;
